import React, { forwardRef, useRef } from "react";
import { bool, element, func, node, string } from "prop-types";
import uuid from "uuid/v4";

import { VisuallyHidden } from "../style-utilities/visually-hidden";
import { overlayClassName } from "./constants/overlay";
import backIcon from "./assets";
import * as S from "./styled";

const SidePanel = forwardRef(
  (
    {
      children,
      description,
      headline,
      id,
      illustration,
      isOpen,
      onBackButtonClick,
      onRequestClose,
      primaryActionButton,
      secondaryActionButton,
      ...passThroughProps
    },
    ref
  ) => {
    const ariaPrefix = useRef(id || uuid());
    const describedById = description && `${ariaPrefix.current}-description`;
    const labelledById = `${ariaPrefix.current}-label`;

    return (
      <>
        <S.OverlayGlobalStyle />
        <S.Modal
          ref={ref}
          ariaDescribedBy={describedById}
          ariaLabelledBy={labelledById}
          closeTimeoutMS={500}
          id={id}
          isOpen={isOpen}
          onRequestClose={onRequestClose}
          overlayClassName={overlayClassName}
          {...passThroughProps}
        >
          <S.SidePanelWrapper>
            <S.CloseButton onClick={onRequestClose} />
            {onBackButtonClick && (
              <S.BackButton onClick={onBackButtonClick}>
                <VisuallyHidden>back</VisuallyHidden>
                <img alt="" src={backIcon} />
              </S.BackButton>
            )}
            {illustration && (
              <S.IllustrationWrapper>{illustration}</S.IllustrationWrapper>
            )}
            <S.Headline id={labelledById}>{headline}</S.Headline>
            {description && (
              <S.Description id={describedById}>{description}</S.Description>
            )}

            {children && <S.Content>{children}</S.Content>}

            {(secondaryActionButton || primaryActionButton) && (
              <S.Footer>
                {secondaryActionButton && (
                  <S.SecondaryAction>{secondaryActionButton}</S.SecondaryAction>
                )}
                {primaryActionButton && (
                  <S.PrimaryAction>{primaryActionButton}</S.PrimaryAction>
                )}
              </S.Footer>
            )}
          </S.SidePanelWrapper>
        </S.Modal>
      </>
    );
  }
);

SidePanel.propTypes = {
  /**
   * The SidePanel's content
   * */
  children: node,
  /**
   * Adds a description block to the SidePanel
   */
  description: string,
  /**
   * Headline for the SidePanel
   */
  headline: string.isRequired,
  /**
   * The SidePanel's ID. Also is used to generate aria-describledby and aria-labelledby attributes
   */
  id: string,
  /**
   * A component that defines the illustration for the SidePanel.
   */
  illustration: node,
  /**
   * Determines if Modal is open or not
   */
  isOpen: bool.isRequired,
  /**
   * A function called when Back button is clicked. If it is provided then the Back button will be drawn
   */
  onBackButtonClick: func,
  /**
   * A function called when the modal's overlay or close button is clicked.
   */
  onRequestClose: func.isRequired,
  /**
   * A button that represents the SidePanel's primary action. It's recommended to use one of the
   * `PrimaryButton` or `DangerButton` components
   */
  primaryActionButton: element,
  /**
   * A button that represents the SidePanel's secondary action. It's recommended to use
   * `SecondaryButton` component
   */
  secondaryActionButton: element
};

SidePanel.displayName = "SidePanel";

SidePanel.defaultProps = {
  children: null,
  description: null,
  id: null,
  illustration: null,
  onBackButtonClick: null,
  primaryActionButton: null,
  secondaryActionButton: null
};

export default SidePanel;
